// v1 data utilities — self-contained, no dependency on legacy App.js
export const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
export const MONTH_ORDER = Object.fromEntries(MONTHS.map((m, i) => [m, i + 1]));
export const MONTH_FULL = { Jan: "January", Feb: "February", Mar: "March", Apr: "April", May: "May", Jun: "June", Jul: "July", Aug: "August", Sep: "September", Oct: "October", Nov: "November", Dec: "December" };
export const CURRENCY_SYMBOLS = { USD: "$", GBP: "£", EUR: "€" };

export const KPI_DEFS = [
  { label: "Total Revenue", key: "Total revenue", isCurrency: true },
  { label: "Total Traffic", key: "Sessions" },
  { label: "Conversion Rate", key: "Conversion Rate", isPercent: true },
  { label: "Avg Order Value", key: "Avg Order Value", isCurrency: true },
  { label: "Cart Abandonment", key: "Cart Abandonment Rate", isPercent: true, lowerIsBetter: true },
];

export const parseNumber = (val) => {
  if (val === null || val === undefined || val === "") return null;
  const num = parseFloat(String(val).replace(/[$£€,\s]/g, "").replace("%", ""));
  return isNaN(num) ? null : num;
};

export const calcChange = (current, previous) => {
  const c = parseNumber(current);
  const p = parseNumber(previous);
  if (c === null || p === null || p === 0) return null;
  return ((c - p) / p) * 100;
};

export const formatNumber = (value, { isCurrency = false, isPercent = false, currency = "USD" } = {}) => {
  const num = parseNumber(value);
  if (num === null) return "—";
  if (isPercent) return `${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  if (isCurrency) return `${CURRENCY_SYMBOLS[currency] || "$"}${num.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  return num.toLocaleString(undefined, { maximumFractionDigits: 0 });
};

// Compact "4.5K" style for big KPI cards
export const formatCompact = (value, { isCurrency = false, isPercent = false, currency = "USD" } = {}) => {
  const num = parseNumber(value);
  if (num === null) return "—";
  if (isPercent) return `${num.toLocaleString(undefined, { maximumFractionDigits: 2 })}%`;
  const sym = isCurrency ? CURRENCY_SYMBOLS[currency] || "$" : "";
  if (Math.abs(num) >= 1000000) return `${sym}${(num / 1000000).toFixed(1)}M`;
  if (Math.abs(num) >= 10000) return `${sym}${(num / 1000).toFixed(1)}K`;
  return `${sym}${num.toLocaleString(undefined, { maximumFractionDigits: num < 100 ? 2 : 0 })}`;
};

// Parse "Nov 2025" / "Nov-25" / "nov-2025" -> {month:"Nov", year:"2025"}
export const parseYearMonth = (val) => {
  if (!val || typeof val !== "string") return null;
  const parts = val.trim().replace(/[-\s]+/, " ").split(" ");
  if (parts.length < 2) return null;
  let month = parts[0].substring(0, 3);
  month = month.charAt(0).toUpperCase() + month.slice(1).toLowerCase();
  let year = parts[1];
  if (year.length === 2) year = "20" + year;
  return MONTH_ORDER[month] ? { month, year } : null;
};

export const sortRowsByMonth = (rows) =>
  [...rows].sort((a, b) => {
    const aP = parseYearMonth(a["Year month"]);
    const bP = parseYearMonth(b["Year month"]);
    if (!aP || !bP) return 0;
    if (aP.year !== bP.year) return parseInt(aP.year) - parseInt(bP.year);
    return MONTH_ORDER[aP.month] - MONTH_ORDER[bP.month];
  });

export const findRowForMonth = (rows, month, year) =>
  rows.find((r) => {
    const p = parseYearMonth(r["Year month"]);
    return p && p.month === month && p.year === String(year);
  }) || null;

export const prevMonthOf = (month, year) => {
  const idx = MONTHS.indexOf(month);
  return idx === 0 ? { month: "Dec", year: String(parseInt(year) - 1) } : { month: MONTHS[idx - 1], year: String(year) };
};

// Classify parsed CSV by headers
export const classifyCsv = (headers) => {
  const lower = headers.map((h) => h.toLowerCase());
  const hasChannel = lower.some((h) => h.includes("channel group") || h === "channel");
  const hasMonth = lower.some((h) => h.includes("year month") || h === "month");
  if (hasChannel && hasMonth) return "organic"; // monthly channel history → organic trend source
  if (hasChannel) return "channel";
  if (hasMonth) return "kpi";
  return "unknown";
};

export const CHANNEL_NAME_KEY = "Session default channel group";

// Top-N channels + aggregated Others
export const topChannels = (rows, valueKey = "Total revenue", n = 6) => {
  const items = (rows || [])
    .map((c, i) => ({ name: c[CHANNEL_NAME_KEY] || `Channel ${i}`, value: parseNumber(c[valueKey]) || 0, row: c }))
    .filter((d) => d.value > 0)
    .sort((a, b) => b.value - a.value);
  if (items.length <= n) return items;
  const top = items.slice(0, n);
  const othersValue = items.slice(n).reduce((s, d) => s + d.value, 0);
  if (othersValue > 0) top.push({ name: "Others", value: othersValue, row: null });
  return top;
};

export const findChannelRow = (rows, name) =>
  (rows || []).find((r) => (r[CHANNEL_NAME_KEY] || "").trim().toLowerCase() === name.trim().toLowerCase()) || null;

export const channelMixPercent = (rows, name, valueKey = "Total revenue") => {
  if (!rows || rows.length === 0) return null;
  const total = rows.reduce((s, r) => s + (parseNumber(r[valueKey]) || 0), 0);
  if (!total) return null;
  const row = findChannelRow(rows, name);
  if (!row) return null;
  return ((parseNumber(row[valueKey]) || 0) / total) * 100;
};
